import type {
  FeePlacement,
  Loan,
  LoanMath,
  LoanResult,
  ParticipantPayout,
  StaffMember,
} from '../types/commission'

/** Total borrower-facing fee, always $1,995 regardless of where it sits. */
export const STANDARD_FEE = 1995
/** UWM threshold — anything collected under this is treated as a short. */
export const UWM_THRESHOLD = 1195
/** Full comp on a file is 3 points; every rate is a share of this. */
export const FULL_COMP_BPS = 300

export const TIERS = [
  { tier: 1, min: 1, max: 3, bps: 100, label: '1–3 loans' },
  { tier: 2, min: 4, max: 5, bps: 125, label: '4–5 loans' },
  { tier: 3, min: 6, max: Infinity, bps: 150, label: '6+ loans' },
] as const

export function tierFor(priorMonthLoans: number) {
  return (
    TIERS.find((t) => priorMonthLoans >= t.min && priorMonthLoans <= t.max) ?? TIERS[0]
  )
}

export function feePlacementFor(state: string): FeePlacement {
  if (state === 'NJ') return 'application'
  if (state === 'NC') return 'underwriting'
  return 'processing'
}

export const FEE_PLACEMENT_LABEL: Record<FeePlacement, string> = {
  application: 'Application fee',
  underwriting: 'Underwriting fee',
  processing: 'Processing fee',
}

/** The rate a person is entitled to before the file's actual points are applied. */
export function baselineBpsFor(member: StaffMember): number {
  if (member.role === 'LOA') return member.flatBps ?? 0
  return tierFor(member.priorMonthLoans).bps
}

export function computeLoanMath(loan: Loan): LoanMath {
  const placement = feePlacementFor(loan.state)
  const standardProcessing = placement === 'processing' ? STANDARD_FEE : 0
  const standardUnderwriting = placement === 'underwriting' ? STANDARD_FEE : 0

  const credits = [
    { label: 'YSPL', amount: loan.ysplDollars },
    { label: 'Processing fee collected', amount: loan.processingFeeCollected },
    { label: 'Origination fee', amount: loan.originationFee },
    { label: 'Discount points', amount: loan.discountPoints },
  ]
  const debits = [
    { label: 'Standard underwriting fee', amount: standardUnderwriting },
    { label: 'Standard processing fee', amount: standardProcessing },
    { label: 'Tolerance cure', amount: loan.toleranceCure },
  ]

  const creditTotal = credits.reduce((sum, line) => sum + line.amount, 0)
  const debitTotal = debits.reduce((sum, line) => sum + line.amount, 0)
  const net = creditTotal - debitTotal
  const actualBps = loan.loanAmount > 0 ? (net / loan.loanAmount) * 10000 : 0

  const feeCollected =
    loan.processingFeeCollected +
    loan.underwritingFeeCollected +
    loan.applicationFeeCollected

  return {
    credits,
    debits,
    creditTotal,
    debitTotal,
    net,
    actualBps,
    feePlacement: placement,
    feePlacementLabel: FEE_PLACEMENT_LABEL[placement],
    feeCollected,
    isPointShort: actualBps < FULL_COMP_BPS - 0.001,
    isFeeShort: feeCollected < STANDARD_FEE,
    isUnderUwmThreshold: feeCollected < UWM_THRESHOLD,
  }
}

export function computeLoanResult(
  loan: Loan,
  staff: StaffMember[],
  overrides: Record<string, number> = {},
): LoanResult {
  const math = computeLoanMath(loan)

  const payouts: ParticipantPayout[] = loan.participants.flatMap((participant) => {
    const member = staff.find((s) => s.id === participant.staffId)
    if (!member) return []

    const baselineBps = baselineBpsFor(member)
    const override = overrides[participant.staffId] ?? participant.overrideBps
    const entitledBps = override ?? baselineBps
    const shareOfFullComp = entitledBps / FULL_COMP_BPS
    const payoutBps = shareOfFullComp * math.actualBps

    return [
      {
        staff: member,
        entitledBps,
        isOverridden: override !== undefined && override !== baselineBps,
        baselineBps,
        shareOfFullComp,
        payoutBps,
        payoutDollars: (payoutBps / 10000) * loan.loanAmount,
      },
    ]
  })

  return {
    loan,
    math,
    payouts,
    payoutTotal: payouts.reduce((sum, p) => sum + p.payoutDollars, 0),
  }
}
